import { useEffect, useRef } from 'react';

import { ACCESS_TOKEN, ACCESS_TOKEN_PHOENIX } from '@legacy/constants/cookies';
import Cookies from 'js-cookie';
import { useAuth } from 'react-oidc-context';
import { useNavigate, Outlet, useLocation } from 'react-router';

import { getAuthCookieOptions } from '@/auth/authCookies';
import { userManager } from '@/auth/userManager';
import { isTest } from '@/legacy/utils/env';

/**
 * ProtectedRoute Component
 *
 * This component handles authentication protection for routes by:
 * 1. Redirecting unauthenticated users to the login page via signinRedirect
 * 2. Handling the post-authentication navigation via url_state
 * 3. Showing a loading state during authentication
 *
 * Authentication Flow:
 * - When a user attempts to access a protected route, they are redirected to login
 * - The current path is saved as url_state in the authentication request
 * - After successful login, the user is redirected to the saved url_state
 */
export const ProtectedRoute = () => {
  if (isTest) return <Outlet />;

  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const signInInitiatedRef = useRef(false);

  // Check if the current route is the logout route (logoff for legacy)
  const isLogoutRoute = pathname === '/logout' || pathname === '/logoff';

  useEffect(() => {
    // Skip navigation if we're on the logout route
    if (isLogoutRoute) return;

    if (user && user.url_state) {
      // Navigate to the user's url_state if it exists
      navigate(user.url_state);
    }
  }, [user?.url_state]);

  useEffect(() => {
    if (
      isLoading ||
      isAuthenticated ||
      isLogoutRoute ||
      signInInitiatedRef.current
    ) {
      return;
    }
    signInInitiatedRef.current = true;
    const returnTo = pathname + search;
    const cookieOptions = getAuthCookieOptions();
    Cookies.remove(ACCESS_TOKEN_PHOENIX, cookieOptions);
    Cookies.remove(ACCESS_TOKEN, cookieOptions);
    userManager.removeUser().finally(() => {
      userManager.signinRedirect({ url_state: returnTo });
    });
  }, [isAuthenticated, isLoading, isLogoutRoute, pathname, search]);

  // Show nothing during authentication to prevent flashing of content
  if (isLoading) {
    // TODO: Add loading spinner or similar?
    return null;
  }

  if (!isAuthenticated && !isLogoutRoute) {
    return null;
  }

  // User is authenticated and not in a loading state, render the route
  return <Outlet />;
};
